export interface Point {
  x: number;
  y: number;
}

export interface MirrorVisual {
  a: Point;
  b: Point;
  required?: boolean;
}

export interface HazardVisual {
  kind: 'segment' | 'circle' | 'polygon';
  a?: Point;
  b?: Point;
  center?: Point;
  radius?: number;
  points?: Point[];
}

export interface ReceiverVisual {
  center: Point;
  radius: number;
  current?: boolean;
  pulse?: number;
}

export interface ShardVisual {
  center: Point;
  radius: number;
  collected: boolean;
}

export interface PulseVisual {
  position: Point;
  velocity: Point;
  radius: number;
  moving: boolean;
  trail: Point[];
}

export interface PreviewVisual {
  points: Point[];
  valid: boolean;
  bounces: number;
}

export interface RenderState {
  mode: 'title' | 'ready' | 'aiming' | 'flying' | 'transition' | 'paused' | 'gameover';
  time: number;
  current: ReceiverVisual | null;
  target: ReceiverVisual | null;
  mirrors: MirrorVisual[];
  hazards: HazardVisual[];
  shards: ShardVisual[];
  pulse: PulseVisual | null;
  preview: PreviewVisual | null;
  pressure: number;
  focus: number;
  overdrive: number;
  relay: number;
  roomOffset?: number;
}

export interface FrameMetrics {
  sampleCount: number;
  averageMs: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
  longFrames: number;
  dpr: number;
  particles: number;
}

type ParticleKind = 'cyan' | 'amber' | 'rose' | 'glass' | 'white';

interface Particle {
  active: boolean;
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  drag: number;
  gravity: number;
  kind: ParticleKind;
}

interface Star {
  x: number;
  y: number;
  depth: number;
  size: number;
  phase: number;
}

const WORLD_WIDTH = 390;
const WORLD_HEIGHT = 844;
const PARTICLE_LIMIT = 168;
const palette: Record<ParticleKind, string> = {
  cyan: '#75f6ff',
  amber: '#ffc278',
  rose: '#ff6d8c',
  glass: '#a8cad2',
  white: '#f3ffff',
};

function clamp(value: number, min = 0, max = 1): number {
  return Math.max(min, Math.min(max, value));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function percentile(values: number[], amount: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * amount))] ?? 0;
}

export class GameRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly particles: Particle[];
  private readonly stars: Star[];
  private readonly frameSamples: number[] = [];
  private cssWidth = WORLD_WIDTH;
  private cssHeight = WORLD_HEIGHT;
  private scale = 1;
  private offsetX = 0;
  private offsetY = 0;
  private dpr = 1;
  private shake = 0;
  private flash = 0;
  private flashKind: ParticleKind = 'white';
  private reducedMotion = false;
  private background: HTMLCanvasElement | null = null;
  private resizeQueued = false;

  constructor(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!ctx) throw new Error('Canvas 2D is unavailable');
    this.canvas = canvas;
    this.ctx = ctx;
    this.particles = Array.from({ length: PARTICLE_LIMIT }, () => ({
      active: false,
      x: 0,
      y: 0,
      previousX: 0,
      previousY: 0,
      vx: 0,
      vy: 0,
      life: 0,
      maxLife: 0,
      size: 1,
      drag: 0.96,
      gravity: 0,
      kind: 'cyan',
    }));
    this.stars = Array.from({ length: 46 }, (_, index) => ({
      x: ((index * 89.17) % WORLD_WIDTH),
      y: ((index * 151.31) % WORLD_HEIGHT),
      depth: 0.18 + ((index * 47) % 100) / 125,
      size: 0.35 + ((index * 31) % 11) / 10,
      phase: index * 0.83,
    }));
    this.resize();
    const observer = new ResizeObserver(() => this.queueResize());
    observer.observe(canvas);
    window.visualViewport?.addEventListener('resize', () => this.queueResize(), { passive: true });
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
  }

  screenToWorld(clientX: number, clientY: number): Point {
    const bounds = this.canvas.getBoundingClientRect();
    return {
      x: (clientX - bounds.left - this.offsetX) / this.scale,
      y: (clientY - bounds.top - this.offsetY) / this.scale,
    };
  }

  kick(strength = 1): void {
    if (this.reducedMotion) return;
    this.shake = Math.min(9, this.shake + strength * 4.5);
  }

  flashScreen(kind: ParticleKind = 'white', strength = 1): void {
    this.flashKind = kind;
    this.flash = Math.max(this.flash, this.reducedMotion ? strength * 0.12 : strength * 0.45);
  }

  burst(position: Point, kind: ParticleKind, count: number, energy = 1): void {
    const actualCount = Math.ceil(count * (this.reducedMotion ? 0.4 : 1));
    for (let i = 0; i < actualCount; i += 1) {
      const particle = this.particles.find((candidate) => !candidate.active);
      if (!particle) break;
      const angle = (i / Math.max(1, actualCount)) * Math.PI * 2 + Math.random() * 0.45;
      const speed = (32 + Math.random() * 130) * energy;
      particle.active = true;
      particle.x = position.x;
      particle.y = position.y;
      particle.previousX = position.x;
      particle.previousY = position.y;
      particle.vx = Math.cos(angle) * speed;
      particle.vy = Math.sin(angle) * speed;
      particle.life = particle.maxLife = 0.26 + Math.random() * 0.48;
      particle.size = 0.7 + Math.random() * 2.5;
      particle.drag = 0.925 + Math.random() * 0.045;
      particle.gravity = kind === 'glass' ? 70 : 0;
      particle.kind = kind;
    }
  }

  clearEffects(): void {
    for (const particle of this.particles) particle.active = false;
    this.shake = 0;
    this.flash = 0;
  }

  render(state: RenderState, deltaSeconds: number): void {
    const dt = Math.min(0.05, Math.max(0, deltaSeconds));
    this.recordFrame(dt * 1000);
    this.updateEffects(dt);

    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(this.getBackground(), 0, 0, this.canvas.width, this.canvas.height);

    const shakeX = this.reducedMotion ? 0 : (Math.sin(state.time * 73) * this.shake + Math.sin(state.time * 41) * this.shake * 0.35);
    const shakeY = this.reducedMotion ? 0 : (Math.cos(state.time * 67) * this.shake * 0.75);
    ctx.setTransform(this.dpr * this.scale, 0, 0, this.dpr * this.scale, this.dpr * (this.offsetX + shakeX), this.dpr * (this.offsetY + shakeY));

    this.drawAmbient(state);
    if (state.mode !== 'title') {
      this.drawPressure(state);
      const roomOffset = state.roomOffset ?? 0;
      ctx.save();
      ctx.translate(0, roomOffset);
      this.drawArchitecture(state);
      this.drawShards(state.shards, state.time);
      if (state.preview) this.drawPreview(state.preview, state.time, state.focus);
      if (state.pulse) this.drawPulse(state.pulse, state.time, state.overdrive);
      this.drawParticles();
      ctx.restore();
      this.drawFocusVignette(state.focus, state.overdrive);
    } else {
      this.drawTitleConstellation(state.time);
      this.drawParticles();
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (this.flash > 0.01) {
      ctx.globalAlpha = clamp(this.flash, 0, this.reducedMotion ? 0.12 : 0.42);
      ctx.fillStyle = palette[this.flashKind];
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.globalAlpha = 1;
    }
  }

  getMetrics(): FrameMetrics {
    const values = this.frameSamples;
    const average = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
    return {
      sampleCount: values.length,
      averageMs: average,
      p95Ms: percentile(values, 0.95),
      p99Ms: percentile(values, 0.99),
      maxMs: values.length ? Math.max(...values) : 0,
      longFrames: values.filter((value) => value > 50).length,
      dpr: this.dpr,
      particles: this.particles.filter((particle) => particle.active).length,
    };
  }

  private resize(): void {
    const bounds = this.canvas.getBoundingClientRect();
    this.cssWidth = Math.max(1, bounds.width || window.innerWidth);
    this.cssHeight = Math.max(1, bounds.height || window.innerHeight);
    this.scale = Math.min(this.cssWidth / WORLD_WIDTH, this.cssHeight / WORLD_HEIGHT);
    this.offsetX = (this.cssWidth - WORLD_WIDTH * this.scale) * 0.5;
    this.offsetY = (this.cssHeight - WORLD_HEIGHT * this.scale) * 0.5;
    this.dpr = Math.min(1.5, Math.max(1, window.devicePixelRatio || 1));
    this.canvas.width = Math.round(this.cssWidth * this.dpr);
    this.canvas.height = Math.round(this.cssHeight * this.dpr);
    this.background = null;
  }

  private queueResize(): void {
    if (this.resizeQueued) return;
    this.resizeQueued = true;
    requestAnimationFrame(() => {
      this.resizeQueued = false;
      this.resize();
    });
  }

  private getBackground(): HTMLCanvasElement {
    if (this.background && this.background.width === this.canvas.width && this.background.height === this.canvas.height) return this.background;
    const background = document.createElement('canvas');
    background.width = this.canvas.width;
    background.height = this.canvas.height;
    const ctx = background.getContext('2d');
    if (!ctx) return background;

    const gradient = ctx.createRadialGradient(background.width * 0.35, background.height * 0.2, 0, background.width * 0.5, background.height * 0.45, Math.max(background.width, background.height) * 0.82);
    gradient.addColorStop(0, '#12202d');
    gradient.addColorStop(0.3, '#09121b');
    gradient.addColorStop(0.72, '#05080e');
    gradient.addColorStop(1, '#020307');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, background.width, background.height);

    const light = ctx.createLinearGradient(0, 0, background.width, background.height);
    light.addColorStop(0, 'rgba(104,232,242,.035)');
    light.addColorStop(0.52, 'rgba(0,0,0,0)');
    light.addColorStop(1, 'rgba(255,156,104,.025)');
    ctx.fillStyle = light;
    ctx.fillRect(0, 0, background.width, background.height);
    this.background = background;
    return background;
  }

  private updateEffects(dt: number): void {
    this.shake *= Math.pow(0.002, dt);
    this.flash *= Math.pow(0.0002, dt);
    for (const particle of this.particles) {
      if (!particle.active) continue;
      particle.previousX = particle.x;
      particle.previousY = particle.y;
      particle.vx *= Math.pow(particle.drag, dt * 60);
      particle.vy = particle.vy * Math.pow(particle.drag, dt * 60) + particle.gravity * dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.life -= dt;
      if (particle.life <= 0) particle.active = false;
    }
  }

  private drawAmbient(state: RenderState): void {
    const ctx = this.ctx;
    ctx.save();
    for (const star of this.stars) {
      const drift = state.mode === 'flying' ? 18 : 4;
      const y = (star.y + state.time * drift * star.depth) % WORLD_HEIGHT;
      const x = star.x + Math.sin(state.time * 0.12 + star.phase) * 5 * star.depth;
      const alpha = 0.08 + (Math.sin(state.time * 1.2 + star.phase) * 0.5 + 0.5) * 0.18;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = star.depth > .7 ? '#b8fbff' : '#6e929d';
      ctx.beginPath();
      ctx.arc(x, y, star.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(121,215,224,.035)';
    ctx.lineWidth = 1;
    for (let x = -60; x < WORLD_WIDTH + 100; x += 54) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + 92, WORLD_HEIGHT);
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawTitleConstellation(time: number): void {
    const ctx = this.ctx;
    const nodes = [
      { x: 35, y: 700 }, { x: 120, y: 588 }, { x: 72, y: 462 }, { x: 304, y: 382 }, { x: 268, y: 205 }, { x: 356, y: 104 },
    ];
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(100,229,239,.12)';
    ctx.lineWidth = 18;
    ctx.beginPath();
    ctx.moveTo(nodes[0]?.x ?? 0, nodes[0]?.y ?? 0);
    for (const node of nodes.slice(1)) ctx.lineTo(node.x, node.y);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(142,251,250,.48)';
    ctx.lineWidth = 1.4;
    ctx.setLineDash([2, 9]);
    ctx.lineDashOffset = -time * 16;
    ctx.stroke();
    ctx.setLineDash([]);
    for (let index = 0; index < nodes.length; index += 1) {
      const node = nodes[index];
      if (!node) continue;
      const pulse = 1 + Math.sin(time * 1.7 + index) * 0.12;
      this.drawReceiver({ center: node, radius: (index === 3 ? 20 : 9) * pulse, current: index === 0 }, time + index);
    }
    const progress = (time * 0.12) % (nodes.length - 1);
    const segment = Math.floor(progress);
    const local = progress - segment;
    const from = nodes[segment];
    const to = nodes[segment + 1];
    if (from && to) {
      const point = { x: lerp(from.x, to.x, local), y: lerp(from.y, to.y, local) };
      this.drawPulse({ position: point, velocity: { x: to.x - from.x, y: to.y - from.y }, radius: 4, moving: true, trail: [] }, time, 0);
    }
    ctx.restore();
  }

  private drawPressure(state: RenderState): void {
    const ctx = this.ctx;
    const pressure = clamp(state.pressure);
    const top = lerp(WORLD_HEIGHT + 65, WORLD_HEIGHT * 0.53, pressure);
    const gradient = ctx.createLinearGradient(0, top - 100, 0, WORLD_HEIGHT);
    gradient.addColorStop(0, 'rgba(255,57,95,0)');
    gradient.addColorStop(.35, `rgba(160,18,54,${0.07 + pressure * 0.1})`);
    gradient.addColorStop(1, `rgba(13,0,9,${0.72 + pressure * 0.2})`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, top - 100, WORLD_WIDTH, WORLD_HEIGHT - top + 100);

    ctx.save();
    ctx.strokeStyle = `rgba(255,91,122,${0.22 + pressure * 0.45})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(0, top);
    const slices = 16;
    for (let i = 1; i <= slices; i += 1) {
      const x = (i / slices) * WORLD_WIDTH;
      const y = top + Math.sin(i * 2.7 + state.time * 1.8) * (3 + pressure * 6);
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.restore();
  }

  private drawArchitecture(state: RenderState): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.lineCap = 'round';
    for (const mirror of state.mirrors) this.drawMirror(mirror, state.time);
    for (const hazard of state.hazards) this.drawHazard(hazard, state.time);
    if (state.current) this.drawReceiver({ ...state.current, current: true }, state.time);
    if (state.target) this.drawReceiver(state.target, state.time + 1.7);
    ctx.restore();
  }

  private drawMirror(mirror: MirrorVisual, time: number): void {
    const ctx = this.ctx;
    const dx = mirror.b.x - mirror.a.x;
    const dy = mirror.b.y - mirror.a.y;
    const length = Math.hypot(dx, dy);
    const nx = length ? -dy / length : 0;
    const ny = length ? dx / length : 0;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = mirror.required ? 'rgba(255,190,111,.16)' : 'rgba(109,231,242,.12)';
    ctx.lineWidth = 13;
    ctx.beginPath();
    ctx.moveTo(mirror.a.x, mirror.a.y);
    ctx.lineTo(mirror.b.x, mirror.b.y);
    ctx.stroke();
    ctx.strokeStyle = mirror.required ? '#ffc37d' : '#a2faff';
    ctx.globalAlpha = .72;
    ctx.lineWidth = 2.2;
    ctx.stroke();
    ctx.globalAlpha = .25;
    ctx.lineWidth = .75;
    for (let position = 13 + ((time * 18) % 22); position < length - 8; position += 22) {
      const t = position / length;
      const x = lerp(mirror.a.x, mirror.b.x, t);
      const y = lerp(mirror.a.y, mirror.b.y, t);
      ctx.beginPath();
      ctx.moveTo(x - nx * 5, y - ny * 5);
      ctx.lineTo(x + nx * 5, y + ny * 5);
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawHazard(hazard: HazardVisual, time: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = 'rgba(255,91,125,.9)';
    ctx.fillStyle = 'rgba(255,50,94,.08)';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 7]);
    ctx.lineDashOffset = -time * 11;
    ctx.beginPath();
    if (hazard.kind === 'segment' && hazard.a && hazard.b) {
      ctx.moveTo(hazard.a.x, hazard.a.y);
      ctx.lineTo(hazard.b.x, hazard.b.y);
    } else if (hazard.kind === 'circle' && hazard.center && hazard.radius) {
      ctx.arc(hazard.center.x, hazard.center.y, hazard.radius, 0, Math.PI * 2);
    } else if (hazard.kind === 'polygon' && hazard.points?.length) {
      const first = hazard.points[0];
      if (first) ctx.moveTo(first.x, first.y);
      for (const point of hazard.points.slice(1)) ctx.lineTo(point.x, point.y);
      ctx.closePath();
    }
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  private drawReceiver(receiver: ReceiverVisual, time: number): void {
    const ctx = this.ctx;
    const { x, y } = receiver.center;
    const radius = receiver.radius;
    const pulse = receiver.pulse ?? 0;
    const accent = receiver.current ? '#78f7ff' : '#ffbd72';
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(time * (receiver.current ? -0.18 : 0.24));
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = .16 + pulse * .08;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 10 + pulse * 4;
    ctx.beginPath();
    ctx.arc(0, 0, radius + 5 + pulse * 3, .15, 1.75);
    ctx.arc(0, 0, radius + 5 + pulse * 3, Math.PI + .15, Math.PI + 1.75);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = receiver.current ? 'rgba(120,247,255,.88)' : 'rgba(255,193,118,.92)';
    ctx.lineWidth = receiver.current ? 2.4 : 3;
    ctx.beginPath();
    ctx.arc(0, 0, radius + 5 + pulse * 3, .15, 1.75);
    ctx.arc(0, 0, radius + 5 + pulse * 3, Math.PI + .15, Math.PI + 1.75);
    ctx.stroke();
    ctx.globalAlpha = .28;
    ctx.lineWidth = .8;
    ctx.beginPath();
    ctx.arc(0, 0, radius - 4, 0, Math.PI * 2);
    ctx.stroke();
    ctx.rotate(-time * (receiver.current ? -.18 : .24));
    ctx.globalAlpha = .68;
    ctx.fillStyle = accent;
    const marker = receiver.current ? 3.2 : 4;
    ctx.beginPath();
    if (receiver.current) {
      ctx.arc(0, 0, marker, 0, Math.PI * 2);
    } else {
      ctx.moveTo(0, -marker * 1.3);
      ctx.lineTo(marker, 0);
      ctx.lineTo(0, marker * 1.3);
      ctx.lineTo(-marker, 0);
      ctx.closePath();
    }
    ctx.fill();
    ctx.restore();
  }

  private drawShards(shards: ShardVisual[], time: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let index = 0; index < shards.length; index += 1) {
      const shard = shards[index];
      if (!shard || shard.collected) continue;
      ctx.save();
      ctx.translate(shard.center.x, shard.center.y);
      ctx.rotate(time * 0.65 + index * 1.2);
      const glow = .65 + Math.sin(time * 2.8 + index) * .25;
      ctx.globalAlpha = glow;
      ctx.fillStyle = '#b8fff5';
      ctx.beginPath();
      ctx.moveTo(0, -shard.radius * 1.3);
      ctx.lineTo(shard.radius * .72, 0);
      ctx.lineTo(0, shard.radius * 1.3);
      ctx.lineTo(-shard.radius * .72, 0);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
  }

  private drawPreview(preview: PreviewVisual, time: number, focus: number): void {
    if (preview.points.length < 2) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([2, 8]);
    ctx.lineDashOffset = -time * 22;
    ctx.strokeStyle = preview.valid ? `rgba(151,255,247,${.48 + focus * .26})` : 'rgba(255,112,142,.62)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    const first = preview.points[0];
    if (first) ctx.moveTo(first.x, first.y);
    for (const point of preview.points.slice(1)) ctx.lineTo(point.x, point.y);
    ctx.stroke();
    ctx.setLineDash([]);
    for (const point of preview.points.slice(1, -1)) {
      ctx.fillStyle = preview.valid ? '#adfff7' : '#ff7894';
      ctx.beginPath();
      ctx.arc(point.x, point.y, 2.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawPulse(pulse: PulseVisual, time: number, overdrive: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    if (pulse.trail.length > 1) {
      for (let index = 1; index < pulse.trail.length; index += 1) {
        const from = pulse.trail[index - 1];
        const to = pulse.trail[index];
        if (!from || !to) continue;
        const amount = index / pulse.trail.length;
        ctx.globalAlpha = amount * (this.reducedMotion ? .24 : .5);
        ctx.strokeStyle = overdrive > 0 ? '#d0acff' : '#6ff6ff';
        ctx.lineWidth = .5 + amount * 3.2;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
    }
    const breathing = pulse.moving ? 1 : 1 + Math.sin(time * 3.2) * .12;
    const radius = pulse.radius * breathing;
    ctx.globalAlpha = .2;
    ctx.fillStyle = overdrive > 0 ? '#b990ff' : '#75f6ff';
    ctx.beginPath();
    ctx.arc(pulse.position.x, pulse.position.y, radius * 3.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    const core = ctx.createRadialGradient(pulse.position.x - 1, pulse.position.y - 1, 0, pulse.position.x, pulse.position.y, radius * 1.2);
    core.addColorStop(0, '#ffffff');
    core.addColorStop(.42, overdrive > 0 ? '#e8dbff' : '#cafffa');
    core.addColorStop(1, overdrive > 0 ? '#986cff' : '#5beaf5');
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(pulse.position.x, pulse.position.y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawParticles(): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    for (const particle of this.particles) {
      if (!particle.active) continue;
      const amount = clamp(particle.life / particle.maxLife);
      ctx.globalAlpha = amount * amount;
      ctx.strokeStyle = palette[particle.kind];
      ctx.fillStyle = palette[particle.kind];
      ctx.lineWidth = Math.max(.4, particle.size * amount);
      ctx.beginPath();
      ctx.moveTo(particle.previousX, particle.previousY);
      ctx.lineTo(particle.x, particle.y);
      ctx.stroke();
      if (particle.size > 1.6) {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * .42 * amount, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }

  private drawFocusVignette(focus: number, overdrive: number): void {
    const ctx = this.ctx;
    if (focus <= .01 && overdrive <= .01) return;
    ctx.save();
    const amount = clamp(focus * .75 + overdrive * .4);
    const gradient = ctx.createRadialGradient(WORLD_WIDTH * .5, WORLD_HEIGHT * .48, 100, WORLD_WIDTH * .5, WORLD_HEIGHT * .48, WORLD_HEIGHT * .62);
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(.62, `rgba(34,94,108,${amount * .05})`);
    gradient.addColorStop(1, overdrive > 0 ? `rgba(87,42,133,${amount * .25})` : `rgba(0,7,14,${amount * .52})`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    ctx.restore();
  }

  private recordFrame(milliseconds: number): void {
    if (milliseconds <= 0 || milliseconds > 1000) return;
    this.frameSamples.push(milliseconds);
    if (this.frameSamples.length > 900) this.frameSamples.splice(0, this.frameSamples.length - 900);
  }
}
